/**
 * 追问文本协议与状态、注解解析、overlay 几何计算。
 * 移植自 COSMO-Sphere-Client：
 *  - apps/electron/src/renderer/components/app-shell/ChatDisplay.follow-ups.ts
 *  - packages/ui/src/components/chat/follow-up-helpers.ts
 *  - packages/ui/src/components/annotations/follow-up-state.ts
 *  - packages/ui/src/components/markdown/annotation-resolver.ts
 *  - packages/ui/src/components/annotations/annotation-overlay-geometry.ts
 */
import {
  getClientRectsForOffsets,
  collectTextSegments,
  consolidateRectsByLine
} from './annotation-core'

export const FOLLOW_UP_HEADER_REGEX = /(?:\*\*Follow-ups\*\*|Follow-up annotations:)/i
// 分隔符必须锚定在换行后的行首：协议固定为 `[#n] {quote}\n→ {note}`，
// 避免 quote 正文里自带的「→」（如「毛利率 15.34% → 14.63%」）被误判为 quote/note 分隔符。
const FOLLOW_UP_ITEM_REGEX = /[ \t]*>?[ \t]*\[#(\d+)\][ \t]*([\s\S]*?)\r?\n[ \t]*>?[ \t]*→[ \t]*([\s\S]*?)(?=(?:\r?\n[ \t]*---[ \t]*(?:\r?\n|$))|$)/g

export function normalizeFollowUpText(text) {
  if (!text) return ''
  return String(text).replace(/\s+/g, ' ').trim()
}

export function truncateForChipTooltip(text, maxLength = 180) {
  const normalized = normalizeFollowUpText(text)
  if (!normalized) return ''
  if (normalized.length <= maxLength) return normalized
  return `${normalized.slice(0, maxLength - 1)}…`
}

export function getAnnotationNoteText(annotation) {
  if (!annotation || !Array.isArray(annotation.body)) return ''
  const note = annotation.body.find((item) => item && item.type === 'note')
  return note && typeof note.text === 'string' ? note.text : ''
}

export function isAnnotationFollowUpSent(annotation) {
  const followUp = annotation && annotation.meta && annotation.meta.followUp
  if (!followUp) return false
  const note = normalizeFollowUpText(getAnnotationNoteText(annotation))
  const lastSentText = normalizeFollowUpText(followUp.lastSentText || '')
  if (!lastSentText) return false
  return Boolean(lastSentText) && note === lastSentText
}

/**
 * 'none' | 'pending' | 'sent'
 */
export function getAnnotationFollowUpState(annotation) {
  const followUp = annotation && annotation.meta && annotation.meta.followUp
  if (!followUp) return 'none'
  const note = normalizeFollowUpText(getAnnotationNoteText(annotation))
  if (!note) return 'none'
  return isAnnotationFollowUpSent(annotation) ? 'sent' : 'pending'
}

export function formatAnnotationFollowUpTooltipText(annotation, maxLength = 180) {
  const followUp = annotation && annotation.meta && annotation.meta.followUp
  if (!followUp) return ''
  const note = getAnnotationNoteText(annotation) || followUp.text || ''
  const quote = annotation && annotation.target ? annotation.target.quote || '' : ''
  const state = getAnnotationFollowUpState(annotation)
  const summary = truncateForChipTooltip(note, maxLength)
  if (!summary) return ''
  if (state === 'sent') return `已发送追问：${summary}`
  const quoteSummary = truncateForChipTooltip(quote, 60)
  return quoteSummary ? `追问（引用：${quoteSummary}）：${summary}` : `追问：${summary}`
}

/**
 * 拼接发送给后端的「追问段」。
 * followUps: [{ index, quote, note }]
 */
export function formatFollowUpSection(followUps, options = {}) {
  if (!Array.isArray(followUps) || followUps.length === 0) return ''
  const { includeTopSeparator = false } = options
  const lines = followUps
    .map((item) => {
      const quote = String(item.quote || '').replace(/\s+/g, ' ').trim()
      const note = String(item.note || '').replace(/\s+/g, ' ').trim()
      if (!quote && !note) return ''
      return `[#${item.index}] ${quote}\n→ ${note}`
    })
    .filter(Boolean)
  if (lines.length === 0) return ''
  const body = `**Follow-ups**\n\n${lines.join('\n\n---\n\n')}`
  return includeTopSeparator ? `---\n\n${body}` : body
}

/**
 * 规范化已拼接的 follow-ups markdown（去掉条目之间多余的 --- 分隔等）。
 */
export function normalizeFollowUpsMarkdown(message) {
  if (!message || !FOLLOW_UP_HEADER_REGEX.test(message)) return message
  const match = message.match(FOLLOW_UP_HEADER_REGEX)
  if (!match || match.index == null) return message
  const head = message.slice(0, match.index).replace(/\s*---\s*$/, '').trim()
  const tail = message.slice(match.index)
  const items = []
  FOLLOW_UP_ITEM_REGEX.lastIndex = 0
  let item
  while ((item = FOLLOW_UP_ITEM_REGEX.exec(tail)) !== null) {
    const index = Number(item[1])
    const quote = String(item[2] || '').replace(/\s+/g, ' ').trim()
    const note = String(item[3] || '').replace(/\s+/g, ' ').trim()
    if (!quote && !note) continue
    items.push({ index, quote, note })
  }
  if (items.length === 0) return message
  const section = formatFollowUpSection(items)
  return head ? `${head}\n\n---\n\n${section}` : section
}

/**
 * 从用户消息文本中拆出正文与追问条目。
 * 返回 { body, items: [{ index, quote, note }] }
 */
export function splitFollowUpsSection(content) {
  if (!content || typeof content !== 'string') {
    return { body: content || '', items: [] }
  }
  const match = content.match(FOLLOW_UP_HEADER_REGEX)
  if (!match || match.index == null) {
    return { body: content, items: [] }
  }
  const rawBody = content.slice(0, match.index)
  const tail = content.slice(match.index)
  const items = []
  FOLLOW_UP_ITEM_REGEX.lastIndex = 0
  let item
  while ((item = FOLLOW_UP_ITEM_REGEX.exec(tail)) !== null) {
    const index = Number(item[1])
    const quote = String(item[2] || '').replace(/\s+/g, ' ').trim()
    const note = String(item[3] || '').replace(/\s+/g, ' ').trim()
    if (!quote && !note) continue
    items.push({ index, quote, note })
  }
  if (items.length === 0) {
    return { body: content, items: [] }
  }
  const body = rawBody.replace(/\s*---\s*$/, '').trim()
  return { body, items }
}

/* ---------------- annotation resolve ---------------- */

function resolveByQuote(fullText, quote) {
  if (!quote) return null
  const directIndex = fullText.indexOf(quote)
  if (directIndex >= 0) {
    return { start: directIndex, end: directIndex + quote.length }
  }
  // 空白归一化回退
  const normalizedQuote = normalizeFollowUpText(quote)
  if (!normalizedQuote) return null
  const charMap = []
  let normalized = ''
  for (let i = 0; i < fullText.length; i += 1) {
    const char = fullText[i]
    if (/\s/.test(char)) {
      if (normalized && normalized[normalized.length - 1] !== ' ') {
        charMap.push(i)
        normalized += ' '
      }
      continue
    }
    charMap.push(i)
    normalized += char
  }
  const normalizedIndex = normalized.indexOf(normalizedQuote)
  if (normalizedIndex < 0) return null
  const start = charMap[normalizedIndex]
  const lastMapped = charMap[Math.min(normalizedIndex + normalizedQuote.length, charMap.length) - 1]
  if (start == null || lastMapped == null) return null
  return { start, end: lastMapped + 1 }
}

/**
 * 解析文本注解在 fullText 中的位置，返回 [{ annotation, start, end }]
 */
export function resolveTextAnnotations(fullText, annotations) {
  if (!fullText || !Array.isArray(annotations) || annotations.length === 0) return []
  const resolved = []
  annotations.forEach((annotation) => {
    if (!annotation || !annotation.target) return
    const position = annotation.target.position
    if (
      position &&
      typeof position.start === 'number' &&
      typeof position.end === 'number' &&
      position.start >= 0 &&
      position.end <= fullText.length &&
      position.end > position.start
    ) {
      resolved.push({ annotation, start: position.start, end: position.end })
      return
    }
    const quote = annotation.target.quote
    const byQuote = resolveByQuote(fullText, quote)
    if (byQuote) {
      resolved.push({ annotation, start: byQuote.start, end: byQuote.end })
    }
  })
  return resolved.sort((a, b) => a.start - b.start)
}

/* ---------------- overlay geometry ---------------- */

/**
 * 计算高亮 rect 与序号角标位置（相对 root）。
 * 返回 { rects: [{key,left,top,width,height,state,annotation}], chips: [{key,left,top,index,state,annotation}], unresolved: [] }
 */
export function computeAnnotationOverlayGeometry({
  root,
  resolvedAnnotations,
  annotationIndexOverrides
}) {
  const result = { rects: [], chips: [], unresolved: [] }
  if (!root || !resolvedAnnotations || resolvedAnnotations.length === 0) return result

  const rootRect = root.getBoundingClientRect()
  const segments = collectTextSegments(root)

  resolvedAnnotations.forEach((entry, order) => {
    const { annotation, start, end } = entry
    const clientRects = getClientRectsForOffsets(root, segments, start, end)
    if (clientRects.length === 0) {
      result.unresolved.push(entry)
      return
    }
    const merged = consolidateRectsByLine(clientRects)
    const state = getAnnotationFollowUpState(annotation)
    const index =
      annotationIndexOverrides && annotationIndexOverrides[annotation.id]
        ? annotationIndexOverrides[annotation.id]
        : order + 1
    const relativeRects = merged.map((rect, i) => ({
      key: `${annotation.id}-${i}`,
      left: rect.left - rootRect.left,
      top: rect.top - rootRect.top,
      width: rect.width,
      height: rect.height,
      state,
      annotation
    }))
    result.rects.push(...relativeRects)

    const firstLine = merged[0]
    const lastRectOfFirstLine = merged
      .filter((rect) => Math.abs(rect.top - firstLine.top) <= 2)
      .reduce((acc, rect) => (acc && acc.left + acc.width >= rect.left + rect.width ? acc : rect), null)
    const anchor = lastRectOfFirstLine || firstLine
    result.chips.push({
      key: `${annotation.id}-chip`,
      left: anchor.left + anchor.width - rootRect.left,
      top: anchor.top - rootRect.top,
      index,
      state,
      annotation
    })
  })

  return result
}

/**
 * 角标配色（对应 annotation-style-tokens.getAnnotationChipVisual）
 */
export function getAnnotationChipVisual(state) {
  if (state === 'pending') {
    return {
      background: 'color-mix(in srgb, var(--info) 34%, var(--background))',
      color: 'rgba(15,23,42,0.95)',
      cursor: 'pointer'
    }
  }
  if (state === 'sent') {
    return {
      background: 'color-mix(in srgb, var(--info) 14%, var(--background))',
      color: 'rgba(15,23,42,0.95)',
      cursor: 'default'
    }
  }
  return {
    background: 'color-mix(in srgb, var(--info) 30%, var(--background))',
    color: 'rgba(15,23,42,0.95)',
    cursor: 'pointer'
  }
}

export function getAnnotationRectVisual(state) {
  const base = 'color-mix(in srgb, var(--info) 10%, transparent)'
  return {
    background: base,
    opacity: state === 'sent' ? 0.58 : 1
  }
}
